import api from '../../services/api';

export interface User {
    id: string;
    username: string;
    email: string;
    tenantId: string;
    role?: string;
    name?: string;
    avatarUrl?: string;
    token?: string; // Add token to User
}

export interface LoginRequest {
    email: string;
    password: string;
}

export interface RegisterRequest {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
}

export interface AuthResponse {
    user: User;
    token?: string; // Add token to response
    refreshToken?: string; // Add refreshToken
}

class AuthService {
    getCurrentUser(): User | null {
        const userStr = localStorage.getItem('saas_user_data');
        if (!userStr) return null;
        try {
            return JSON.parse(userStr);
        } catch {
            return null;
        }
    }

    setCurrentUser(user: User): void {
        localStorage.setItem('saas_user_data', JSON.stringify(user));
        // Also store token if present
        if (user.token) {
            localStorage.setItem('access_token', user.token);
        }
    }

    removeCurrentUser(): void {
        localStorage.removeItem('saas_user_data');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
    }

    async login(credentials: LoginRequest): Promise<AuthResponse> {
        const response = await api.post<any>('/auth/login', credentials);
        const { user, token, refreshToken } = response.data;
        
        // Store token with user data
        const userWithToken = { ...user, token };
        this.setCurrentUser(userWithToken);
        
        // Also store in 'user' key for interceptor compatibility
        localStorage.setItem('user', JSON.stringify({ token, user }));
        
        if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
        }
        
        return { user: userWithToken, token, refreshToken };
    }

    async register(data: RegisterRequest): Promise<AuthResponse> {
        const response = await api.post<any>('/auth/register', data);
        const { user, token, refreshToken } = response.data;
        
        const userWithToken = { ...user, token };
        this.setCurrentUser(userWithToken);
        localStorage.setItem('user', JSON.stringify({ token, user }));
        
        if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
        }
        
        return { user: userWithToken, token, refreshToken };
    }

    async logout(): Promise<void> {
        try {
            await api.post('/auth/logout');
        } finally {
            this.removeCurrentUser();
            // Optional: Redirect or reload
        }
    }

    async checkAuth(): Promise<User | null> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = await api.get<any>('/auth/me');
            const user: User = {
                id: response.data.userId,
                email: response.data.email,
                username: response.data.username,
                tenantId: response.data.tenantId,
                role: response.data.role
            };
            this.setCurrentUser(user);
            return user;
        } catch {
            this.removeCurrentUser();
            return null;
        }
    }
}

export const authService = new AuthService();
export default authService;
